import React, { useMemo, useRef, useState } from 'react'
import { ClickAwayListener, makeStyles, Popper, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { formatCurrency, getTierPrices, setSelectedTier, TIER_LABELS, TIER_ORDER } from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import classNames from 'classnames'
import { useQuote } from '../../../../context'
import PackageCompareDialog from './PackageCompareDialog'
import { closeOnEscape } from './shared'
import { ChevronDownIcon, ChevronLeftIcon, PackageIcon } from '../../../../options/TieredPackagePresetOptions/icons'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      // The block owns its own padding so the hover fill reaches the card's edges, which is why
      // the total tables hand it an unpadded slot.
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 1.5)
      },
      // The whole block is the switch control, so the target is the block rather than a small link.
      clickable: {
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': {
          background: neutral.P98
        }
      },
      labelRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        color: neutral.P60
      },
      label: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: 'normal',
        letterSpacing: 0.7,
        color: 'inherit',
        whiteSpace: 'nowrap'
      },
      // Hidden in print/PDF, where it can't be used.
      switch: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        color: 'inherit',
        '@media print': {
          display: 'none'
        }
      },
      chevron: {
        display: 'inline-flex',
        flexShrink: 0
      },
      chevronOpen: {
        transform: 'rotate(180deg)'
      },
      // Customer-facing: the same glyph turned to point at what it opens, since Switch there raises
      // the Compare plans dialog rather than expanding a menu in place.
      chevronRight: {
        transform: 'rotate(180deg)'
      },
      tierRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        minWidth: 0
      },
      // The identity and the delta sit against opposite edges.
      tierRowSplit: {
        justifyContent: 'space-between',
        gap: theme.spacing(2)
      },
      tierIdentity: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        minWidth: 0
      },
      // What this package adds to, or takes off, the estimate as built. Carries the weight and the
      // only colour in the block, because it is the number the customer is deciding on. The nearest
      // token to the design's own #366d8e.
      delta: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: 'normal',
        color: theme.paintscout.palette.semantic.Info.S50,
        whiteSpace: 'nowrap',
        flexShrink: 0
      },
      icon: {
        display: 'inline-flex',
        flexShrink: 0,
        color: neutral.P40
      },
      menuIcon: {
        display: 'inline-flex',
        flexShrink: 0,
        color: neutral.P40
      },
      tierName: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: 'normal',
        letterSpacing: 0.7,
        color: neutral.P40,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      // The package's slot, so a renamed package is still placeable against the other two. Set
      // back from the name in both size and weight — it labels the name, it does not compete.
      tierLevel: {
        fontSize: theme.typography.pxToRem(12),
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: 'normal',
        marginTop: '2px',
        color: neutral.P70,
        whiteSpace: 'nowrap',
        flexShrink: 0
      },

      /**
       * Placed by Popper rather than absolutely: the total card and its scroll containers clip
       * their overflow, and this menu has to hang past the bottom of the card.
       */
      menu: {
        padding: 6,
        background: theme.palette.common.white,
        border: `1px solid ${neutral.P85}`,
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(20, 30, 50, 0.14)',
        '@media print': {
          display: 'none'
        }
      },
      menuItem: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: 10,
        borderRadius: 6,
        cursor: 'pointer',
        '&:hover': {
          background: neutral.P98
        }
      },
      menuItemSelected: {
        background: neutral.P98
      },
      menuItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0
      },
      menuItemText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
      },
      menuItemName: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: '16px',
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      menuItemLevel: {
        fontSize: theme.typography.pxToRem(12),
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: '16px',
        color: neutral.P70,
        whiteSpace: 'nowrap'
      },
      menuItemPrice: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: '16px',
        color: neutral.P60,
        whiteSpace: 'nowrap',
        flexShrink: 0
      }
    }
  },
  { name: 'PackageSelectedRow' }
)

/**
 * The "Package" summary inside the Total card: which package the estimate is priced as, and a
 * Switch control that drops down the others with their prices.
 *
 * Picking one applies it through `setSelectedTier` and the surrounding quote context — persisted in
 * the editor, recomputed locally in the customer and preview views. Renders nothing when packages
 * aren't enabled.
 */
export default function PackageSelectedRow() {
  const classes = useStyles({})
  const theme = useTheme()
  const { quote, updateQuote, isEditable } = useQuote()
  const { options } = useClientOptions()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const tierPrices = useMemo(() => getTierPrices({ quote }), [quote])
  const tieredPackages = quote.tieredPackages

  if (!tieredPackages?.enabled || !tierPrices) {
    return null
  }

  // Customers lose the Switch once they've responded (accepted/declined) or the quote became an
  // invoice — mirrors the read-only lock on the gate cards. The contractor editor
  // (isEditable) can always switch, regardless of status.
  const isLocked = quote.status?.value === 'accepted' || quote.status?.value === 'declined' || !!quote.is_invoice
  const canSwitch = isEditable || !isLocked

  const selectedTierId = (tieredPackages.selectedTierId ?? tieredPackages.baseTierId) as TieredPackageTierId
  const selected = tierPrices[selectedTierId]
  if (!selected) {
    return null
  }

  // Switched-off packages are not offered, the same as everywhere else the customer meets them.
  const offered = TIER_ORDER.filter((tierId) => !tierPrices[tierId].isDisabled)

  const handleSelect = (tierId: TieredPackageTierId) => {
    setOpen(false)
    if (tierId !== selectedTierId) {
      updateQuote({ quote: setSelectedTier({ quote, options, tierId }) })
    }
  }

  /*
   * Two surfaces, two controls (ENG-1542).
   *
   * The estimator's editor keeps the dropdown: they are adjusting a document they already understand,
   * and a menu of prices is the quickest way to try another package. The customer and the preview of
   * what the customer sees get the Compare plans dialog instead — someone deciding between packages
   * needs to read them side by side, which a dropdown cannot show. `isEditable` is the seam because
   * it is already what separates the two here (see `canSwitch`).
   */
  const isCustomerFacing = !isEditable

  /*
   * What this package does to the estimate as built: `$0` on the base package, otherwise a signed
   * amount. Both audiences get it (ENG-1628) — the estimator switching packages needs to read what
   * the switch costs as much as the customer choosing one does. The editor's row also keeps the
   * package's slot beside the name.
   */
  const delta = selected.delta
  const deltaLabel =
    delta === 0
      ? formatCurrency({ options, value: 0, abbreviationPostfix: '' })
      : `${delta > 0 ? '+' : '-'}${formatCurrency({ options, value: Math.abs(delta) })}`

  const row = (
    <>
      <div className={classes.labelRow}>
        <span className={classes.label}>{isCustomerFacing ? 'Package Selected' : 'Package'}</span>
        {canSwitch && (
          <span className={classes.switch}>
            <span className={classes.label}>Switch</span>
            {isCustomerFacing ? (
              <span className={classNames(classes.chevron, classes.chevronRight)}>
                <ChevronLeftIcon size={16} />
              </span>
            ) : (
              <span className={classNames(classes.chevron, { [classes.chevronOpen]: open })}>
                <ChevronDownIcon size={16} />
              </span>
            )}
          </span>
        )}
      </div>

      <div className={classNames(classes.tierRow, classes.tierRowSplit)}>
        <span className={classes.tierIdentity}>
          <span className={classes.icon}>
            <PackageIcon
              icon={tieredPackages.tiers?.[selectedTierId]?.icon}
              size={16}
              placeholderColor={theme.paintscout.palette.brand.Neutral.P40}
            />
          </span>
          <span className={classes.tierName}>{selected.name || TIER_LABELS[selectedTierId]}</span>
          {!isCustomerFacing && <span className={classes.tierLevel}>{TIER_LABELS[selectedTierId]}</span>}
        </span>
        <span className={classes.delta}>{deltaLabel}</span>
      </div>
    </>
  )

  if (!canSwitch) {
    return <div className={classes.root}>{row}</div>
  }

  if (isCustomerFacing) {
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          aria-label="Switch package"
          className={classNames(classes.root, classes.clickable)}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setOpen(true)
            }
          }}
        >
          {row}
        </div>

        <PackageCompareDialog open={open} onClose={() => setOpen(false)} />
      </>
    )
  }

  return (
    <>
      <div
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label="Switch package"
        className={classNames(classes.root, classes.clickable)}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((wasOpen) => !wasOpen)
          }
        }}
      >
        {row}
      </div>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
        modifiers={{
          offset: { enabled: true, offset: '0, 4' },
          preventOverflow: { enabled: true, boundariesElement: 'viewport' }
        }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div
            role="menu"
            className={classes.menu}
            style={{ width: anchorRef.current?.offsetWidth }}
            onKeyDown={closeOnEscape(() => setOpen(false))}
          >
            {offered.map((tierId) => (
              <div
                key={tierId}
                role="menuitem"
                tabIndex={0}
                className={classNames(classes.menuItem, {
                  [classes.menuItemSelected]: tierId === selectedTierId
                })}
                onClick={() => handleSelect(tierId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSelect(tierId)
                  }
                }}
              >
                <span className={classes.menuItemLeft}>
                  <span className={classes.menuIcon}>
                    <PackageIcon
                      icon={tieredPackages.tiers?.[tierId]?.icon}
                      size={16}
                      placeholderColor={theme.paintscout.palette.brand.Neutral.P40}
                    />
                  </span>
                  <span className={classes.menuItemText}>
                    <span className={classes.menuItemName}>{tierPrices[tierId].name || TIER_LABELS[tierId]}</span>
                    <span className={classes.menuItemLevel}>{TIER_LABELS[tierId]}</span>
                  </span>
                </span>
                <span className={classes.menuItemPrice}>
                  {formatCurrency({ options, value: tierPrices[tierId].price })}
                </span>
              </div>
            ))}
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
